import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortablePickingLineItem } from './SortablePickingLineItem';
import { PickingLineItem } from './PickingLineItem';
import { PalletSummaryDto } from '../../../../types/picking';
import { PickingLine } from './types';
import { AlertTriangle, Package } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { applyStructureCaps } from './structureCaps';

/**
 * Strukturvarer: gruppehode for hovedvaren. Hovedvaren plukkes aldri manuelt — den viser
 * fremdrift i komplette sett, avledet fra komponentplukket (min over komponentene av
 * floor(plukket / antall pr. sett)). Komponentlinjene rendres innrykket rett under.
 */
const StructureGroupHeader: React.FC<{ line: PickingLine; components: PickingLine[] }> = ({ line, components }) => {
  const { t } = useTranslation();

  // Summer plukket per komponent (samme komponent kan ha flere plukklinjer ved flerbinge/manko)
  const byComponent = new Map<string, { perUnit: number; picked: number }>();
  components.forEach(c => {
    const entry = byComponent.get(c.itemNo) ?? { perUnit: 0, picked: 0 };
    if (!entry.perUnit && c.structureQtyPerUnit && c.structureQtyPerUnit > 0) {
      entry.perUnit = c.structureQtyPerUnit;
    }
    entry.picked += c.qtyPicked;
    byComponent.set(c.itemNo, entry);
  });

  let setsPicked: number | null = null;
  byComponent.forEach(entry => {
    if (entry.perUnit > 0) {
      const sets = Math.floor(entry.picked / entry.perUnit);
      setsPicked = setsPicked === null ? sets : Math.min(setsPicked, sets);
    }
  });

  // Dekning: hvor mange komplette sett strukturen har saldo til (fra ikke-[X]-linjenes Qty).
  // Begrenser målvisningen når en komponent har manko.
  const setsPossible = components.length > 0
    ? components.some(c => c.structureSetsPossible !== undefined)
      ? components.find(c => c.structureSetsPossible !== undefined)!.structureSetsPossible!
      : line.qty
    : line.qty;
  const isLimited = setsPossible < line.qty;

  const hasShortage = components.some(c => c.binCode === '[X]');
  const isComplete = (setsPicked ?? 0) >= Math.min(setsPossible, line.qty);

  return (
    <div className={`rounded-lg border-2 px-4 py-3 ${isComplete ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Package size={20} className={isComplete ? 'text-green-600' : 'text-blue-600'} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {line.itemNo}
              <span className="font-normal text-gray-700 ml-2">{line.itemDescription}</span>
            </div>
            <div className="text-xs text-gray-600">
              {t('warehouse.modules.picking.structure.header', {}, 'Strukturvare — plukk komponentene under')}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {(hasShortage || isLimited) && (
            <span className="flex items-center gap-1 text-xs font-semibold text-amber-700">
              <AlertTriangle size={13} />
              {isLimited
                ? t('warehouse.modules.picking.structure.possibleSets', { '0': String(setsPossible), '1': String(line.qty) }, 'Manko — kun {0} av {1} sett mulig')
                : t('warehouse.modules.picking.structure.shortage', {}, 'Manko på komponent')}
            </span>
          )}
          <span className={`text-sm font-bold ${isComplete ? 'text-green-700' : 'text-blue-700'}`}>
            {t('warehouse.modules.picking.structure.setsPicked', { '0': String(setsPicked ?? 0), '1': String(line.qty) }, '{0} av {1} sett')}
          </span>
        </div>
      </div>
    </div>
  );
};

interface PickingLinesListProps {
  lines: PickingLine[];
  currentLineIndex: number | null;
  activeLineIndex: number | null;
  pallets: PalletSummaryDto[];
  editingLocation: number | null;
  editingQuantity: number | null;
  newLocation: string;
  newQuantity: string;
  showPalletInfo: string | null;
  onLineClick: (index: number) => void;
  onLocationEdit: (lineNo: number, location: string) => void;
  onQuantityEdit: (lineNo: number, quantity: number) => void;
  onStartLocationEdit: (lineNo: number, location: string) => void;
  onStartQuantityEdit: (lineNo: number, quantity: string) => void;
  onLocationChange: (value: string) => void;
  onQuantityChange: (value: string) => void;
  onSplitLine: (lineNo: number) => void;
  onPickLine: (lineNo: number, qty: number) => void;
  onUndoPicking: (lineNo: number) => void;
  onShowPalletInfo: (palletId: string | null) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onMoveToEnd?: (lineNo: number) => void;
  enableDragAndDrop: boolean;
  /** Multi-select for «flytt valgte til slutt»: markerte linjenr. */
  moveSelectedLineNos?: Set<number>;
  /** Multi-select: toggle markering av en linje. */
  onToggleMoveSelect?: (lineNo: number) => void;
  /** Strukturplukk: åpner telle-snarveien (vises kun på begrensede komponentlinjer). */
  onStructureCount?: (lineNo: number) => void;
  /** Slett pall fra pall-lista på en linje (AP5). */
  onDeletePallet?: (sscc: string) => void;
  /** Skjul «Del linje» — settes i D-pak-modus (AP6). */
  hideSplit?: boolean;
  /** D-pak-modus: kompakt pall-teller i stedet for én chip per pall. */
  dpakMode?: boolean;
}

export const PickingLinesList: React.FC<PickingLinesListProps> = ({
  lines,
  currentLineIndex,
  activeLineIndex,
  pallets,
  editingLocation,
  editingQuantity,
  newLocation,
  newQuantity,
  showPalletInfo,
  onLineClick,
  onLocationEdit,
  onQuantityEdit,
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onSplitLine,
  onPickLine,
  onUndoPicking,
  onShowPalletInfo,
  onReorder,
  onMoveToEnd,
  enableDragAndDrop,
  moveSelectedLineNos,
  onToggleMoveSelect,
  onStructureCount,
  onDeletePallet,
  hideSplit,
  dpakMode
}) => {
  const { t } = useTranslation();
  // Manko-seksjon (Ariens): linjene er allerede sortert med [X]-manko nederst (usePickingData).
  // Vis en overskrift rett før første mankolinje. Strukturkomponenter med [X] holdes inne i
  // strukturgruppen sin (reorganizeLines) og skal IKKE trigge seksjonshodet.
  const isPlainShortage = (l: PickingLine) => l.binCode === '[X]' && (l.structureLineType ?? 0) !== 2;
  const firstShortageIndex = lines.findIndex(isPlainShortage);
  const shortageCount = lines.filter(isPlainShortage).length;
  const shortageHeader = (index: number) =>
    index === firstShortageIndex && firstShortageIndex !== -1 ? (
      <div className="flex items-center gap-1.5 pt-3 mt-1 border-t border-amber-200 text-xs font-semibold text-amber-700">
        <AlertTriangle size={13} />
        {t('warehouse.modules.picking.sections.shortage', {}, 'Restordre / manko')} ({shortageCount})
      </div>
    ) : null;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,      // 150ms long-press for mobile - fast response
        tolerance: 5,     // 5px movement tolerance
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lines.findIndex((line) => line.pListLineNo === active.id);
      const newIndex = lines.findIndex((line) => line.pListLineNo === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  const isStructureHeader = (line: PickingLine) => (line.structureLineType ?? 0) === 1;
  const isStructureComponent = (line: PickingLine) => (line.structureLineType ?? 0) === 2;

  // Strukturvarer: «maks mulig sett»-begrensning. Når én komponent har manko som begrenser
  // antall komplette sett, begrenses de andre komponentene til behovet for det antallet —
  // operatøren skal slippe å plukke for mye og legge tilbake i etterkant. Delt logikk med
  // Aktiv plukklinje-kortet og speilet av backend-sperren (EnsureStructurePickAllowed).
  const displayLines = React.useMemo(() => applyStructureCaps(lines), [lines]);

  // Strukturvarer: hovedvaren rendres som gruppehode (ikke plukkbar linje), komponentene
  // rendres innrykket under. reorganizeLines i usePickingData holder blokken samlet.
  const structureComponentsFor = (header: PickingLine): PickingLine[] =>
    displayLines.filter(l =>
      (l.structureLineType ?? 0) === 2 &&
      l.orderNo === header.orderNo &&
      l.structureParentLineNoERP != null &&
      l.structureParentLineNoERP === header.lineNoERP);

  // If drag-and-drop is disabled, render simple list without DnD context
  if (!enableDragAndDrop) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="space-y-2 transition-all duration-500 ease-in-out">
            {displayLines.map((line, index) => (
              <React.Fragment key={line.pListLineNo}>
                {shortageHeader(index)}
                {isStructureHeader(line) ? (
                  <StructureGroupHeader line={line} components={structureComponentsFor(line)} />
                ) : (
                <div className={isStructureComponent(line) ? 'ml-4 pl-3 border-l-2 border-blue-200' : undefined}>
                <PickingLineItem
                line={line}
                index={index}
                isSelected={index === currentLineIndex}
                isActive={index === activeLineIndex}

                pallets={pallets}
                editingLocation={editingLocation}
                editingQuantity={editingQuantity}
                newLocation={newLocation}
                newQuantity={newQuantity}
                showPalletInfo={showPalletInfo}
                onLineClick={onLineClick}
                onLocationEdit={onLocationEdit}
                onQuantityEdit={onQuantityEdit}
                onStartLocationEdit={onStartLocationEdit}
                onStartQuantityEdit={onStartQuantityEdit}
                onLocationChange={onLocationChange}
                onQuantityChange={onQuantityChange}
                onSplitLine={onSplitLine}
                onPickLine={onPickLine}
                onUndoPicking={onUndoPicking}
                onShowPalletInfo={onShowPalletInfo}
                onMoveToEnd={onMoveToEnd}
                isMoveSelected={moveSelectedLineNos?.has(line.pListLineNo) || false}
                onToggleMoveSelect={onToggleMoveSelect}
                onStructureCount={onStructureCount}
                onDeletePallet={onDeletePallet}
                hideSplit={hideSplit}
                dpakMode={dpakMode}
                />
                </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render with drag-and-drop enabled
  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Picking Lines */}
      <div className="p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lines.filter((line) => (line.structureLineType ?? 0) === 0).map((line) => line.pListLineNo)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 transition-all duration-500 ease-in-out">
              {displayLines.map((line, index) => (
                <React.Fragment key={line.pListLineNo}>
                  {shortageHeader(index)}
                  {isStructureHeader(line) ? (
                    <StructureGroupHeader line={line} components={structureComponentsFor(line)} />
                  ) : isStructureComponent(line) ? (
                    // Strukturkomponenter sorteres ikke med dnd — gruppen skal holdes samlet.
                    <div className="ml-4 pl-3 border-l-2 border-blue-200">
                      <PickingLineItem
                        line={line}
                        index={index}
                        isSelected={index === currentLineIndex}
                        isActive={index === activeLineIndex}
                        pallets={pallets}
                        editingLocation={editingLocation}
                        editingQuantity={editingQuantity}
                        newLocation={newLocation}
                        newQuantity={newQuantity}
                        showPalletInfo={showPalletInfo}
                        onLineClick={onLineClick}
                        onLocationEdit={onLocationEdit}
                        onQuantityEdit={onQuantityEdit}
                        onStartLocationEdit={onStartLocationEdit}
                        onStartQuantityEdit={onStartQuantityEdit}
                        onLocationChange={onLocationChange}
                        onQuantityChange={onQuantityChange}
                        onSplitLine={onSplitLine}
                        onPickLine={onPickLine}
                        onUndoPicking={onUndoPicking}
                        onShowPalletInfo={onShowPalletInfo}
                        onMoveToEnd={onMoveToEnd}
                        isMoveSelected={moveSelectedLineNos?.has(line.pListLineNo) || false}
                        onToggleMoveSelect={onToggleMoveSelect}
                        onStructureCount={onStructureCount}
                        onDeletePallet={onDeletePallet}
                        hideSplit={hideSplit}
                        dpakMode={dpakMode}
                      />
                    </div>
                  ) : (
                  <SortablePickingLineItem
                  line={line}
                  index={index}
                  isSelected={index === currentLineIndex}
                  isActive={index === activeLineIndex}
  
                  pallets={pallets}
                  editingLocation={editingLocation}
                  editingQuantity={editingQuantity}
                  newLocation={newLocation}
                  newQuantity={newQuantity}
                  showPalletInfo={showPalletInfo}
                  onLineClick={onLineClick}
                  onLocationEdit={onLocationEdit}
                  onQuantityEdit={onQuantityEdit}
                  onStartLocationEdit={onStartLocationEdit}
                  onStartQuantityEdit={onStartQuantityEdit}
                  onLocationChange={onLocationChange}
                  onQuantityChange={onQuantityChange}
                  onSplitLine={onSplitLine}
                  onPickLine={onPickLine}
                  onUndoPicking={onUndoPicking}
                  onShowPalletInfo={onShowPalletInfo}
                  onMoveToEnd={onMoveToEnd}
                  isMoveSelected={moveSelectedLineNos?.has(line.pListLineNo) || false}
                  onToggleMoveSelect={onToggleMoveSelect}
                  onDeletePallet={onDeletePallet}
                  hideSplit={hideSplit}
                  dpakMode={dpakMode}
                  />
                  )}
                </React.Fragment>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};